import { endOfMonth, startOfMonth } from "date-fns";
import { formatIso8601 } from "@/data/DateTimeUtil";
import {
  ApprovalSummary,
  emptyApprovalSummary,
  sumApprovalSummaries,
  summarizeAbsencePendingDays,
  summarizeTimesheetPendingMinutes,
} from "@/data/ApprovalSummaryUtil";
import { absenceService } from "@/service/AbsenceService";
import { timesheetService } from "@/service/TimesheetService";
import { userService } from "@/service/UserService";
import { User } from "@/api/User";

export type ApprovalSummaryRange = {
  from: string;
  to: string;
  month: Date;
};

export type DirectReportApprovalSummary = {
  reports: User[];
  summaries: Map<number, ApprovalSummary>;
  totals: ApprovalSummary;
};

const monthRange = (month: Date = new Date()): ApprovalSummaryRange => ({
  month,
  from: formatIso8601(startOfMonth(month)),
  to: formatIso8601(endOfMonth(month)),
});

const fetchUserSummary = async (userId: number, range: ApprovalSummaryRange): Promise<ApprovalSummary> => {
  const [timesheets, absences] = await Promise.all([
    timesheetService.findAll(userId, range.from, range.to),
    absenceService.findEntries(userId, range.from, range.to),
  ]);

  return {
    timesheetMinutes: summarizeTimesheetPendingMinutes(timesheets),
    absenceDays: summarizeAbsencePendingDays(absences),
  };
};

const fetchUserSummaries = async (
  userIds: number[],
  range: ApprovalSummaryRange = monthRange()
): Promise<Map<number, ApprovalSummary>> => {
  const entries = await Promise.all(
    userIds.map(async (userId) => [userId, await fetchUserSummary(userId, range)] as const)
  );

  return new Map(entries);
};

const fetchDirectReportSummaries = async (
  managerId: number,
  range: ApprovalSummaryRange = monthRange()
): Promise<DirectReportApprovalSummary> => {
  const response = await userService.listReports();
  const reportsByManager = userService.toNumberArrayMap(response.reports);
  const reportIds = reportsByManager.get(managerId) ?? [];

  if (reportIds.length === 0) {
    return {
      reports: [],
      summaries: new Map(),
      totals: emptyApprovalSummary(),
    };
  }

  const [reports, summaries] = await Promise.all([
    Promise.all(reportIds.map((id) => userService.find(id))),
    fetchUserSummaries(reportIds, range),
  ]);

  return {
    reports: reports.sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)),
    summaries,
    totals: sumApprovalSummaries(summaries.values()),
  };
};

export const approvalSummaryService = {
  monthRange,
  fetchUserSummary,
  fetchUserSummaries,
  fetchDirectReportSummaries,
};
